import React from 'react'
import { Box, Typography, Avatar, Fade, alpha } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * Helper component for enhanced data rows
 * Can be reused in other widgets to display a row with an icon, label and value
 */
const DataRow = ({ icon: Icon, label, value, highlight = false, colors, delay = 0 }) => (
  <Fade in timeout={800} style={{ transitionDelay: `${delay}ms` }}>
    <Box
      role='group'
      aria-label={`${label}: ${value}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 0.75,
        borderRadius: 2,
        backgroundColor: alpha(colors.textGrey, 0.03),
        border: `1px solid ${alpha(colors.textGrey, 0.08)}`,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'default',
        '&:hover': {
          backgroundColor: alpha(colors.textGrey, 0.08),
          borderColor: highlight ? alpha(colors.greenLight, 0.5) : alpha(colors.textGrey, 0.2),
          transform: 'translateY(-1px)',
          boxShadow: `0 2px 8px ${alpha(colors.bgDark, 0.15)}`
        }
      }}
    >
      <Avatar
        variant='rounded'
        sx={{
          bgcolor: highlight ? alpha(colors.greenLight, 0.15) : alpha(colors.textGrey, 0.05),
          color: highlight ? colors.greenLight : alpha(colors.textGrey, 0.7),
          width: 32,
          height: 32,
          mr: 1.25,
          borderRadius: 1.5,
          transition: 'all 0.3s ease'
        }}
      >
        <Icon sx={{ fontSize: '1.1rem' }} />
      </Avatar>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant='caption'
          sx={{
            color: alpha(colors.textGrey, 0.6),
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.6rem',
            letterSpacing: '0.05em',
            display: 'block',
            mb: 0,
            lineHeight: 1
          }}
        >
          {label}
        </Typography>
        <Typography
          variant={highlight ? 'subtitle2' : 'body2'}
          title={value}
          sx={{
            color: highlight ? colors.greenLight : colors.textGrey,
            fontWeight: highlight ? 600 : 400,
            fontSize: highlight ? '0.85rem' : '0.8rem',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  </Fade>
)

DataRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  highlight: PropTypes.bool,
  colors: PropTypes.shape({
    textGrey: PropTypes.string.isRequired,
    greenLight: PropTypes.string.isRequired,
    bgDark: PropTypes.string.isRequired
  }).isRequired,
  delay: PropTypes.number
}

export default DataRow
